import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import {
  Breadcrumb,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderNextNavButton,
  HeaderNav,
  HeaderSidebarTrigger,
  HeaderPrevNavButton,
  useHeaderNav,
} from '@/app/ui/components/header';
import { EvaluationTypeLabelsEnum, AmbiguityTypes, queryKeys } from '@/app/utils/constants';
import { SearchFocusIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { MainScrollArea } from '@/app/ui/components/main';
import { Spinner } from '@/app/ui/components/spinner';
import { useGetAmbiguities } from '@/app/components/features/evaluations/analysis/Ambiguities/useAmbiguity';
import { AmbiguityDetail } from '@/app/components/features/evaluations/analysis/Ambiguities/AmbiguityDetail';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { NotFound } from '@/app/ui/components/not-found';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/_layout/(main)/ambiguities/$ambiguityId',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.tenderHome');
  const tFeature = useTranslations('features.ambiguities');
  const { tenderPackageId, projectId, ambiguityId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));

  const { data: allAmbiguitiesData, isLoading } = useGetAmbiguities({
    ambiguityType: AmbiguityTypes.TENDER_PACKAGE,
    id: tenderPackageId,
  });
  const allStatements = allAmbiguitiesData?.statements;

  const data = useMemo(
    () => allStatements?.find((item) => item.id === ambiguityId),
    [allStatements, ambiguityId],
  );

  const { handlePrevious, handleNext, isPreviousDisabled, isNextDisabled, currentIndex } =
    useHeaderNav(allStatements || [], ambiguityId, (ambiguityId) => ({
      to: '/project/$projectId/tender-packages/$tenderPackageId/ambiguities/$ambiguityId',
      params: { projectId, tenderPackageId, ambiguityId },
    }));

  if (!data && !isLoading) return <NotFound />;

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/tender-packages/$tenderPackageId/ambiguities"
          params={{ projectId, tenderPackageId }}
        />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                {evaluation.name}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem className="md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="md:hidden" />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <HugeiconsIcon icon={SearchFocusIcon} />
                {t('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/ambiguities"
                params={{ projectId, tenderPackageId }}
              >
                {tFeature('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="tabular-nums">#{currentIndex + 1}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderNav>
          <HeaderPrevNavButton onClick={handlePrevious} disabled={isPreviousDisabled} />
          <HeaderNextNavButton onClick={handleNext} disabled={isNextDisabled} />
        </HeaderNav>
        <HeaderSidechatButton />
      </Header>
      <MainScrollArea>
        {isLoading ? (
          <Spinner kind="inset" />
        ) : data ? (
          <AmbiguityDetail
            data={data}
            currentIndex={currentIndex}
            ambiguityType={AmbiguityTypes.TENDER_PACKAGE}
            evaluationId={tenderPackageId}
            queryKeysToInvalidate={[
              [queryKeys.AMBIGUITY_STATEMENTS, AmbiguityTypes.TENDER_PACKAGE, tenderPackageId],
            ]}
          />
        ) : null}
      </MainScrollArea>
    </>
  );
}
